import type { DriveDocumentListItem } from '@platform/documents';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  CreateBundleRequest,
  CreateBundleResponse,
  DeleteEntryRequest,
  GetProfileRequest,
  GetProfileResponse,
  ListBundlesRequest,
  ListBundlesResponse,
  ListProfilesRequest,
  ListProfilesResponse,
  PublishBundleRequest,
  PublishBundleResponse,
  UpdateBundleRequest,
  UpdateBundleResponse,
  UpsertEntryRequest,
  UpsertEntryResponse,
} from '@shared/types/profile';
import type {
  ArchivePromptRequest,
  CreatePromptRequest,
  ImportPromptRequest,
  ListPromptsRequest,
  ListPromptsResponse,
  PromotePromptRequest,
  PromptRecord,
  RollbackPromptRequest,
  UpdatePromptRequest,
} from '@shared/types/prompts';
import type {
  SearchRelationshipsRequest,
  SearchRelationshipsResponse,
  UpsertRelationshipByEmailRequest,
  UpsertRelationshipRequest,
  UpsertRelationshipResponse,
} from '@shared/types/relationships';
import { appendTelemetryHeaders } from '../../services/telemetry';
import { tokenService } from '../../services/tokenService';
import { apiSlice } from './apiSlice';
import { withTransientQueryRetry } from './baseQueryRetry';
import { handleImpersonationAuthError } from './impersonationErrorHandler';

// RPC slice for /rpc/v1 endpoints (chat and other RPC-style calls)
export const prepareRPCHeaders = async (headers: Headers): Promise<Headers> => {
  // Use getActiveToken which returns impersonation token if active
  const token = await tokenService.getActiveToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  appendTelemetryHeaders(headers);
  return headers;
};

const rawBaseQuery = withTransientQueryRetry(
  fetchBaseQuery({
    baseUrl: '/rpc/v1',
    prepareHeaders: prepareRPCHeaders,
  })
);

export const authAwareBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  const status = result.error?.status;

  // Parse JSON error responses if data is a string
  if (result.error && typeof result.error.data === 'string') {
    try {
      const parsed = JSON.parse(result.error.data);
      result.error.data = parsed;
    } catch {
      // If parsing fails, leave data as string
    }
  }

  // 401/419 mean the session is invalid. 403 means the person is authenticated
  // but not authorized; it must never end the session, or a still-valid
  // Stytch session restores the login and the denied request loops.
  if (typeof status === 'number' && [401, 419].includes(status)) {
    // Check if we're impersonating - restore admin session instead of full logout
    const wasImpersonationRestored = handleImpersonationAuthError(api as any, () => {
      // Reset both API caches
      api.dispatch(rpcSlice.util.resetApiState());
      api.dispatch(apiSlice.util.resetApiState());
    });

    if (!wasImpersonationRestored) {
      // Normal session expiry - clear everything
      tokenService.clearTokens({
        expired: true,
        message: 'Your session has expired. Please sign in again.',
      });
    }
  }
  return result;
};

export const rpcSlice = createApi({
  reducerPath: 'rpc',
  baseQuery: authAwareBaseQuery,
  tagTypes: [
    'Chat',
    'PersonContext',
    'Relationships',
    'Prompts',
    'Artifacts',
    'ArtifactVersions',
    'ArtifactFeedback',
    'DataStatus',
    'ArtifactNotes',
  ],
  endpoints: (builder) => ({
    // Relationships: Upsert relationship by email (resolves emails on backend)
    upsertRelationshipByEmail: builder.mutation<
      UpsertRelationshipResponse,
      UpsertRelationshipByEmailRequest
    >({
      query: (body) => ({
        url: '/relationships.upsert_by_email',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Relationships'],
    }),
    // Relationships: Upsert relationship by person IDs
    upsertRelationship: builder.mutation<UpsertRelationshipResponse, UpsertRelationshipRequest>({
      query: (body) => ({
        url: '/relationships.upsert',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Relationships'],
    }),
    // Relationships: Update relationship by ID
    updateRelationship: builder.mutation<
      import('@shared/types/relationships').UpdateRelationshipResponse,
      import('@shared/types/relationships').UpdateRelationshipRequest
    >({
      query: (body) => ({
        url: '/relationships.update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Relationships'],
    }),
    // Relationships: Delete (soft deactivate) relationship by ID
    deleteRelationship: builder.mutation<
      import('@shared/types/relationships').DeleteRelationshipResponse,
      import('@shared/types/relationships').DeleteRelationshipRequest
    >({
      query: (body) => ({
        url: '/relationships.delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Relationships'],
    }),
    // Relationships: Search relationships by person ID
    searchRelationships: builder.query<SearchRelationshipsResponse, SearchRelationshipsRequest>({
      query: (body) => ({
        url: '/relationships.search',
        method: 'POST',
        body,
      }),
      providesTags: ['Relationships'],
    }),
    listPrompts: builder.query<ListPromptsResponse, ListPromptsRequest | undefined>({
      query: (body = { limit: 50 }) => ({
        url: '/prompts.list',
        method: 'POST',
        body,
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map((prompt) => ({ type: 'Prompts' as const, id: prompt.id })),
              { type: 'Prompts' as const, id: 'LIST' },
            ]
          : [{ type: 'Prompts' as const, id: 'LIST' }],
    }),
    getPrompt: builder.query<PromptRecord, { id: string }>({
      query: ({ id }) => ({
        url: '/prompts.get',
        method: 'GET',
        params: { id },
      }),
      providesTags: (result, _err, arg) =>
        result
          ? [{ type: 'Prompts' as const, id: result.id }]
          : [{ type: 'Prompts' as const, id: arg.id }],
    }),
    createPrompt: builder.mutation<PromptRecord, CreatePromptRequest>({
      query: (body) => ({
        url: '/prompts.create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Prompts', id: 'LIST' }],
    }),
    updatePrompt: builder.mutation<PromptRecord, UpdatePromptRequest>({
      query: (body) => ({
        url: '/prompts.update',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [{ type: 'Prompts' as const, id: result.id }]
          : [{ type: 'Prompts' as const, id: 'LIST' }],
    }),
    promotePrompt: builder.mutation<PromptRecord, PromotePromptRequest>({
      query: (body) => ({
        url: '/prompts.promote',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Prompts', id: 'LIST' }],
    }),
    archivePrompt: builder.mutation<PromptRecord, ArchivePromptRequest>({
      query: (body) => ({
        url: '/prompts.archive',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Prompts' as const, id: result.id },
              { type: 'Prompts' as const, id: 'LIST' },
            ]
          : [{ type: 'Prompts', id: 'LIST' }],
    }),
    rollbackPrompt: builder.mutation<PromptRecord, RollbackPromptRequest>({
      query: (body) => ({
        url: '/prompts.rollback',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
              { type: 'Prompts' as const, id: result.id }, // Reactivated prompt
              { type: 'Prompts' as const, id: arg.prompt_id }, // Archived prompt
              { type: 'Prompts' as const, id: 'LIST' },
            ]
          : [{ type: 'Prompts', id: 'LIST' }],
    }),
    importPrompt: builder.mutation<PromptRecord, ImportPromptRequest>({
      query: (body) => ({
        url: '/prompts.import',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Prompts', id: 'LIST' }],
    }),
    // Profile editor endpoints
    listProfiles: builder.query<ListProfilesResponse, ListProfilesRequest | undefined>({
      query: (body) => ({
        url: '/profile_editor.profiles.list',
        method: body ? 'POST' : 'GET',
        ...(body ? { body } : { params: body }),
      }),
      providesTags: ['Prompts'],
    }),
    getProfile: builder.query<GetProfileResponse, GetProfileRequest>({
      query: (body) => ({
        url: '/profile_editor.profiles.get',
        method: 'POST',
        body,
      }),
      providesTags: ['Prompts'],
    }),
    listBundles: builder.query<ListBundlesResponse, ListBundlesRequest | undefined>({
      query: (body) => ({
        url: '/profile_editor.bundles.list',
        method: body ? 'POST' : 'GET',
        ...(body ? { body } : { params: body }),
      }),
      providesTags: ['Prompts'],
    }),
    createBundle: builder.mutation<CreateBundleResponse, CreateBundleRequest>({
      query: (body) => ({
        url: '/profile_editor.bundles.create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
    updateBundle: builder.mutation<UpdateBundleResponse, UpdateBundleRequest>({
      query: (body) => ({
        url: '/profile_editor.bundles.update',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
    archiveBundle: builder.mutation<void, { id: string }>({
      query: (body) => ({
        url: '/profile_editor.bundles.archive',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
    upsertEntry: builder.mutation<UpsertEntryResponse, UpsertEntryRequest>({
      query: (body) => ({
        url: '/profile_editor.entries.upsert',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
    deleteEntry: builder.mutation<void, DeleteEntryRequest>({
      query: (body) => ({
        url: '/profile_editor.entries.delete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
    publishBundle: builder.mutation<PublishBundleResponse, PublishBundleRequest>({
      query: (body) => ({
        url: '/profile_editor.bundles.publish',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Prompts'],
    }),
  }),
});

// Extended endpoints for drive1on1 document batch fetching
export const drive1on1Api = rpcSlice.injectEndpoints({
  endpoints: (builder) => ({
    batchDocuments: builder.query<
      { documents?: Array<DriveDocumentListItem> },
      { userId: string; ids: string[] }
    >({
      query: ({ userId, ids }) => ({
        url: `/drive1on1/users/${encodeURIComponent(userId)}/providers/google-drive/documents:batch`,
        params: { ids: ids.join(',') },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useUpsertRelationshipByEmailMutation,
  useUpsertRelationshipMutation,
  useUpdateRelationshipMutation,
  useDeleteRelationshipMutation,
  useSearchRelationshipsQuery,
  useListPromptsQuery,
  useLazyListPromptsQuery,
  useGetPromptQuery,
  useCreatePromptMutation,
  useUpdatePromptMutation,
  usePromotePromptMutation,
  useArchivePromptMutation,
  useRollbackPromptMutation,
  useImportPromptMutation,
  // Profile editor hooks
  useListProfilesQuery,
  useGetProfileQuery,
  useListBundlesQuery,
  useCreateBundleMutation,
  useUpdateBundleMutation,
  useArchiveBundleMutation,
  useUpsertEntryMutation,
  useDeleteEntryMutation,
  usePublishBundleMutation,
} = rpcSlice;

export const { useLazyBatchDocumentsQuery } = drive1on1Api;
